<template>
  <transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="handleBackdropClick"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        @click="handleBackdropClick"
      />

      <!-- Modal Content -->
      <div
        :class="[
          'relative bg-white rounded-3xl shadow-2xl overflow-hidden transition-all',
          sizeClasses,
          containerClass
        ]"
        :style="containerStyle"
        @click.stop
      >
        <!-- Close Button -->
        <Button
          v-if="showCloseButton"
          @click="handleClose"
          :class="closeButtonClass"
          :icon="X"
          :icon-size="closeButtonIconSize"
          size="sm"
          variant="ghost"
          circular
        />

        <!-- Header Slot -->
        <div v-if="$slots.header" :class="headerClass">
          <slot name="header" />
        </div>

        <!-- Content Slot -->
        <div :class="contentClasses">
          <slot />
        </div>

        <!-- Footer Slot -->
        <div v-if="$slots.footer" class="p-6 border-t border-gray-100">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { X } from "lucide-vue";

export default {
  name: "Modal",
  data() {
    return {
      X
    };
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg", "xl", "full"].includes(value)
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    containerClass: {
      type: String,
      default: ""
    },
    containerStyle: {
      type: [String, Object],
      default: ""
    },
    contentClass: {
      type: String,
      default: ""
    },
    headerClass: {
      type: String,
      default: "p-6 border-b border-gray-100"
    },
    closeButtonClass: {
      type: String,
      default:
        "absolute top-4 right-4 z-20 rounded-full !min-w-0 !w-10 !h-10 !aspect-square flex items-center justify-center"
    },
    closeButtonIconSize: {
      type: Number,
      default: 20
    },
    closeButtonIconClass: {
      type: String,
      default: ""
    },
    scrollable: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    sizeClasses() {
      const sizes = {
        sm: "w-full max-w-sm",
        md: "w-full max-w-md",
        lg: "w-full max-w-lg",
        xl: "w-full max-w-5xl",
        full: "w-full max-w-full"
      };
      return sizes[this.size] || sizes.md;
    },
    contentClasses() {
      return [
        this.scrollable ? "overflow-y-auto" : "",
        this.contentClass || "p-6"
      ]
        .filter(Boolean)
        .join(" ");
    }
  },
  watch: {
    isOpen(newVal) {
      this.setBackgroundScrollLock(newVal);
    }
  },
  mounted() {
    // The modal is often mounted already open (v-if/v-else-if), in which case
    // the isOpen watcher never fires, so lock background scroll here too.
    if (this.isOpen) {
      this.setBackgroundScrollLock(true);
    }
  },
  beforeDestroy() {
    this.setBackgroundScrollLock(false);
  },
  methods: {
    setBackgroundScrollLock(locked) {
      // The page can scroll on either <html> or <body> depending on layout,
      // so lock/unlock both to fully freeze the background while open.
      const value = locked ? "hidden" : "";
      document.documentElement.style.overflow = value;
      document.body.style.overflow = value;
    },
    handleClose() {
      this.$emit("close");
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.handleClose();
      }
    }
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
